"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.pluginManifestUtils = exports.PluginManifestUtils = void 0;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const common_1 = require("../../../../error/common");
const strip_bom_1 = tslib_1.__importDefault(require("strip-bom"));
const path_1 = tslib_1.__importDefault(require("path"));
const ManifestUtils_1 = require("./ManifestUtils");
const utils_1 = require("./utils");
const results_1 = require("../results");
const errors_1 = require("../errors");
const localizeUtils_1 = require("../../../../common/localizeUtils");
const constant_1 = require("../../../configManager/constant");
const os_1 = require("os");
const envFunctionUtils_1 = require("../../../utils/envFunctionUtils");
const utils_2 = require("../../../../common/utils");
const featureFlags_1 = require("../../../../common/featureFlags");
const odrProvider_1 = require("../../../utils/odrProvider");
const constants_1 = require("../../../constants");
class PluginManifestUtils {
    async readPluginManifestFile(path) {
        if (!(await fs_extra_1.default.pathExists(path))) {
            return (0, teamsfx_api_1.err)(new common_1.FileNotFoundError("PluginManifestUtils", path));
        }
        // Be compatible with UTF8-BOM encoding
        // Avoid Unexpected token error at JSON.parse()
        let content = await fs_extra_1.default.readFile(path, { encoding: "utf-8" });
        content = (0, strip_bom_1.default)(content);
        try {
            const manifest = JSON.parse(content);
            return (0, teamsfx_api_1.ok)(manifest);
        }
        catch (e) {
            return (0, teamsfx_api_1.err)(new common_1.JSONSyntaxError(path, e, "PluginManifestUtils"));
        }
    }
    /**
     * Get plugin manifest with env value filled.
     * @param path path of declaraitve Copilot
     * @returns resolved manifest
     */
    async getManifest(path, context) {
        const manifestRes = await this.readPluginManifestFile(path);
        if (manifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(manifestRes.error);
        }
        // Add environment variable keys to telemetry
        const resolvedManifestRes = await (0, utils_1.getResolvedManifest)(JSON.stringify(manifestRes.value), path, envFunctionUtils_1.ManifestType.PluginManifest, context);
        if (resolvedManifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(resolvedManifestRes.error);
        }
        const resolvedManifestString = resolvedManifestRes.value;
        return (0, teamsfx_api_1.ok)(JSON.parse(resolvedManifestString));
    }
    async validateAgainstSchema(plugin, path, context) {
        const manifestRes = await this.getManifest(path, context);
        if (manifestRes.isErr()) {
            return (0, teamsfx_api_1.err)(manifestRes.error);
        }
        try {
            const schemaErrors = await teamsfx_api_1.AppManifestUtils.validateAgainstSchema(manifestRes.value);
            const localMCPPluginErrors = await this.validateLocalMCPPluginRuntimes(manifestRes.value);
            const allErrors = [...schemaErrors, ...localMCPPluginErrors];
            return (0, teamsfx_api_1.ok)({
                id: plugin.id,
                filePath: path,
                validationResult: allErrors,
            });
        }
        catch (e) {
            return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                (0, localizeUtils_1.getLocalizedString)("error.appstudio.validateFetchSchemaFailed", manifestRes.value.$schema, e.message),
            ])));
        }
    }
    async getApiSpecFilePathFromTeamsManifest(manifest, manifestPath) {
        const pluginFilePathRes = await ManifestUtils_1.manifestUtils.getPluginFilePath(manifest, manifestPath);
        if (pluginFilePathRes.isErr()) {
            return (0, teamsfx_api_1.err)(pluginFilePathRes.error);
        }
        const pluginFilePath = pluginFilePathRes.value;
        const pluginContentRes = await this.readPluginManifestFile(pluginFilePath);
        if (pluginContentRes.isErr()) {
            return (0, teamsfx_api_1.err)(pluginContentRes.error);
        }
        const apiSpecFiles = await this.getApiSpecFilePathFromPlugin(pluginContentRes.value, pluginFilePath);
        return (0, teamsfx_api_1.ok)(apiSpecFiles);
    }
    logValidationErrors(validationRes, platform) {
        const validationErrors = validationRes.validationResult;
        const filePath = validationRes.filePath;
        if (validationErrors.length === 0) {
            return "";
        }
        if (platform !== teamsfx_api_1.Platform.CLI) {
            const errors = validationErrors
                .map((error) => {
                return `${constant_1.SummaryConstant.Failed} ${error}`;
            })
                .join(os_1.EOL);
            return ((0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.validatePluginManifest.checkPath", filePath) +
                os_1.EOL +
                errors);
        }
        else {
            const outputMessage = [];
            outputMessage.push({
                content: (0, localizeUtils_1.getDefaultString)("driver.teamsApp.summary.validatePluginManifest.checkPath", filePath) +
                    "\n",
                color: teamsfx_api_1.Colors.BRIGHT_WHITE,
            });
            validationErrors.map((error) => {
                outputMessage.push({ content: `${constant_1.SummaryConstant.Failed} `, color: teamsfx_api_1.Colors.BRIGHT_RED });
                outputMessage.push({
                    content: `${error}\n`,
                    color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                });
            });
            return outputMessage;
        }
    }
    async getDefaultNextAvailableApiSpecPath(apiSpecPath, apiSpecFolder, isKiotaIntegration = false) {
        let isYaml = false;
        try {
            isYaml = !(await (0, utils_2.isJsonSpecFile)(apiSpecPath));
        }
        catch (e) { }
        let openApiSpecFileName = isYaml || featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.KiotaNPMIntegration)
            ? teamsfx_api_1.DefaultApiSpecYamlFileName
            : teamsfx_api_1.DefaultApiSpecJsonFileName;
        // Check if the default file name already exists
        if (!(await fs_extra_1.default.pathExists(path_1.default.join(apiSpecFolder, openApiSpecFileName)))) {
            return path_1.default.join(apiSpecFolder, openApiSpecFileName);
        }
        const openApiSpecFileNamePrefix = openApiSpecFileName.split(".")[0];
        const openApiSpecFileType = openApiSpecFileName.split(".")[1];
        let apiSpecFileNameSuffix = 1;
        openApiSpecFileName = this.getApiSpecFileName(openApiSpecFileNamePrefix, openApiSpecFileType, apiSpecFileNameSuffix, isKiotaIntegration);
        while (await fs_extra_1.default.pathExists(path_1.default.join(apiSpecFolder, openApiSpecFileName))) {
            apiSpecFileNameSuffix++;
            openApiSpecFileName = this.getApiSpecFileName(openApiSpecFileNamePrefix, openApiSpecFileType, apiSpecFileNameSuffix, isKiotaIntegration);
        }
        const openApiSpecFilePath = path_1.default.join(apiSpecFolder, openApiSpecFileName);
        return openApiSpecFilePath;
    }
    getApiSpecFileName(openApiSpecFileNamePrefix, openApiSpecFileType, apiSpecFileNameSuffix, isKiotaIntegration) {
        let openApiSpecFileName;
        if (isKiotaIntegration) {
            const apiSpecNameSplit = openApiSpecFileNamePrefix.split("-");
            openApiSpecFileName = `${apiSpecNameSplit[0]}_${apiSpecFileNameSuffix}-${apiSpecNameSplit[1]}.${openApiSpecFileType}`;
        }
        else {
            openApiSpecFileName = `${openApiSpecFileNamePrefix}_${apiSpecFileNameSuffix}.${openApiSpecFileType}`;
        }
        return openApiSpecFileName;
    }
    async getApiSpecFilePathFromPlugin(plugin, pluginPath) {
        const runtimes = plugin.runtimes;
        const files = [];
        if (!runtimes) {
            return files;
        }
        for (const runtime of runtimes) {
            if (runtime.type === "OpenApi" && runtime.spec?.url) {
                const specFile = path_1.default.resolve(path_1.default.dirname(pluginPath), runtime.spec.url);
                if (await fs_extra_1.default.pathExists(specFile)) {
                    files.push(specFile);
                }
            }
        }
        return files;
    }
    /**
     * Validate LocalPlugin runtimes that use MCP servers.
     * Validates four requirements:
     * 1. local_endpoint must start with mcp://
     * 2. run_for_functions entries must match tool names in MCP server
     * 3. Function names must appear in run_for_functions
     * 4. Functions cannot redefine parameters—must match MCP server
     * @param manifest The plugin manifest to validate
     * @param _context Driver context for logging
     * @returns Array of validation error strings
     */
    async validateLocalMCPPluginRuntimes(manifest) {
        const errors = [];
        if (!manifest.runtimes) {
            return errors;
        }
        const localPluginRuntimes = manifest.runtimes.filter((rt) => rt.type === "LocalPlugin");
        if (localPluginRuntimes.length === 0) {
            return errors;
        }
        let mcpServers = [];
        if (process.platform === "win32") {
            try {
                mcpServers = await odrProvider_1.ODRProvider.listServers();
            }
            catch (error) {
                return errors;
            }
        }
        const mcpServerMap = new Map(mcpServers.map((s) => [s.identifier, s]));
        for (const runtime of localPluginRuntimes) {
            const runtimeIdx = manifest.runtimes.indexOf(runtime);
            const localEndpoint = runtime.spec.local_endpoint;
            // 1. Only check the ones with endpoint starting with mcp://
            if (!localEndpoint.startsWith(constants_1.LocalMcpPrefix)) {
                continue;
            }
            const mcpIdentifier = localEndpoint.replace(new RegExp(`^${constants_1.LocalMcpPrefix}`), "");
            const mcpServer = mcpServerMap.get(mcpIdentifier);
            if (!mcpServer) {
                errors.push(`/runtimes/${runtimeIdx}/spec/local_endpoint: ` +
                    `MCP server "${mcpIdentifier}" not found in ODR provider.`);
                continue;
            }
            const mcpToolNames = new Set(mcpServer.tools.map((t) => t.name));
            // 2. run_for_functions entries must match tool names in MCP server
            runtime.run_for_functions?.forEach((funcName, funcIdx) => {
                if (!mcpToolNames.has(funcName)) {
                    const availableTools = Array.from(mcpToolNames).join(", ");
                    errors.push(`/runtimes/${runtimeIdx}/run_for_functions[${funcIdx}]: ` +
                        `Tool "${funcName}" not found in MCP server "${mcpServer.display_name}". ` +
                        `Available tools: ${availableTools}`);
                }
            });
            // 3. Function names must appear in run_for_functions
            const runForFunctionsSet = new Set(runtime.run_for_functions || []);
            manifest.functions?.forEach((func, funcIdx) => {
                if (mcpToolNames.has(func.name) && !runForFunctionsSet.has(func.name)) {
                    errors.push(`/functions[${funcIdx}]: ` +
                        `Function "${func.name}" exists in MCP server but is not listed in /runtimes/${runtimeIdx}/run_for_functions.`);
                }
            });
            // 4. Functions cannot redefine parameters—must match MCP server
            runtime.run_for_functions?.forEach((funcName) => {
                const manifestFunc = manifest.functions?.find((f) => f.name === funcName);
                const mcpTool = mcpServer.tools.find((t) => t.name === funcName);
                if (manifestFunc && mcpTool) {
                    const paramErrors = this.validateFunctionParameters(manifestFunc, mcpTool, funcName);
                    errors.push(...paramErrors);
                }
            });
        }
        return errors;
    }
    /**
     * Validate that function parameters match exactly between manifest and MCP tool.
     * @param manifestFunc The function definition from the manifest
     * @param mcpTool The tool definition from MCP server
     * @param funcName The function name for error messages
     * @returns Array of validation error strings
     */
    validateFunctionParameters(manifestFunc, mcpTool, funcName) {
        const errors = [];
        const manifestParams = manifestFunc.parameters?.properties || {};
        const mcpParams = mcpTool.inputSchema?.properties || {};
        const manifestRequired = manifestFunc.parameters?.required || [];
        const mcpRequired = mcpTool.inputSchema?.required || [];
        const manifestParamNames = new Set(Object.keys(manifestParams));
        const mcpParamNames = new Set(Object.keys(mcpParams));
        // Extra parameters in manifest not in MCP
        manifestParamNames.forEach((prop) => {
            if (!mcpParamNames.has(prop)) {
                errors.push(`/functions["${funcName}"]/parameters/properties/${prop}: ` +
                    `Parameter not defined in MCP server. Functions cannot redefine parameters.`);
            }
        });
        // Missing parameters from MCP
        mcpParamNames.forEach((prop) => {
            if (!manifestParamNames.has(prop)) {
                errors.push(`/functions["${funcName}"]/parameters/properties/${prop}: ` +
                    `Missing parameter defined in MCP server. All MCP parameters must be included.`);
            }
        });
        // Validate required array matches
        const manifestRequiredSet = new Set(manifestRequired);
        const mcpRequiredSet = new Set(mcpRequired);
        const extraRequired = manifestRequired.filter((r) => !mcpRequiredSet.has(r));
        const missingRequired = mcpRequired.filter((r) => !manifestRequiredSet.has(r));
        if (extraRequired.length > 0) {
            errors.push(`/functions["${funcName}"]/parameters/required: ` +
                `Extra required parameters not in MCP server: ${extraRequired.join(", ")}`);
        }
        if (missingRequired.length > 0) {
            errors.push(`/functions["${funcName}"]/parameters/required: ` +
                `Missing required parameters from MCP server: ${missingRequired.join(", ")}`);
        }
        // Validate parameter types and properties for matching parameters
        manifestParamNames.forEach((prop) => {
            if (mcpParamNames.has(prop)) {
                const manifestParam = manifestParams[prop];
                const mcpParam = mcpParams[prop];
                // Type check
                if (manifestParam.type !== mcpParam.type) {
                    errors.push(`/functions["${funcName}"]/parameters/properties/${prop}/type: ` +
                        `Type mismatch. Manifest has "${String(manifestParam.type)}", ` +
                        `MCP server has "${String(mcpParam.type)}".`);
                }
                // Enum check
                if (manifestParam.enum || mcpParam.enum) {
                    const manifestEnumSorted = manifestParam.enum
                        ? [...manifestParam.enum].sort()
                        : undefined;
                    const mcpEnumSorted = mcpParam.enum ? [...mcpParam.enum].sort() : undefined;
                    if (JSON.stringify(manifestEnumSorted) !== JSON.stringify(mcpEnumSorted)) {
                        errors.push(`/functions["${funcName}"]/parameters/properties/${prop}/enum: ` +
                            `Enum mismatch. Manifest: ${JSON.stringify(manifestParam.enum)}, ` +
                            `MCP: ${JSON.stringify(mcpParam.enum)}.`);
                    }
                }
            }
        });
        return errors;
    }
}
exports.PluginManifestUtils = PluginManifestUtils;
exports.pluginManifestUtils = new PluginManifestUtils();
//# sourceMappingURL=PluginManifestUtils.js.map